import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { getTzSchedule } from '@/lib/utils/schedule';
import type { CalendarGrid } from '@peach/types/schedule/types.js';
import type { Dayjs } from 'dayjs';

type DayjsTimeSlots = Record<string, Record<string, { start: Dayjs; end: Dayjs }[]>>;

interface UseScheduleSelectionProps {
  schedule: CalendarGrid['available'];
  initialDate?: Dayjs | null;
  initialTime?: Dayjs | null;
  onAutoSelectTime?: (date: Dayjs, times: Dayjs[]) => Dayjs | null;
}

interface UseScheduleSelectionReturn {
  selectedDate: Dayjs | null;
  selectedTime: Dayjs | null;
  tzSchedule: DayjsTimeSlots;
  months: string[];
  days: Record<string, string[]>;
  availableDates: Dayjs[];
  availableTimes: Dayjs[];
  setSelectedDate: Dispatch<SetStateAction<Dayjs | null>>;
  setSelectedTime: Dispatch<SetStateAction<Dayjs | null>>;
  handleDateSelect: (date: Dayjs) => void;
  handleTimeSelect: (time: Dayjs) => void;
}

export function useScheduleSelection({
  schedule,
  initialDate = null,
  initialTime = null,
  onAutoSelectTime,
}: UseScheduleSelectionProps): UseScheduleSelectionReturn {
  const { tzSchedule, months, days } = useMemo(() => getTzSchedule(schedule), [schedule]);

  const availableDates = useMemo(() => {
    const dates: Dayjs[] = [];
    months.forEach((month) => {
      days[month]?.forEach((day) => {
        const slots = tzSchedule[month][day];
        if (slots && slots.length > 0) {
          dates.push(slots[0].start);
        }
      });
    });
    return dates;
  }, [months, days, tzSchedule]);

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    initialDate || availableDates[0] || null
  );
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(initialTime);

  const availableTimes = useMemo(() => {
    if (!selectedDate) return [];
    const monthKey = selectedDate.month().toString();
    const dayKey = selectedDate.date().toString();
    return tzSchedule[monthKey]?.[dayKey]?.map((slot) => slot.start) || [];
  }, [selectedDate, tzSchedule]);

  const handleDateSelect = useCallback((date: Dayjs) => {
    setSelectedDate(date);
    setSelectedTime(null);
  }, []);

  const handleTimeSelect = useCallback((time: Dayjs) => {
    setSelectedTime(time);
  }, []);

  // When the schedule changes under an open picker (the booking loop refetches
  // it after every submit), drop any selection the new grid no longer offers —
  // otherwise the confirm button would submit an instant that is already gone.
  // State adjustment during render (not an effect) per React's prop-change
  // pattern; the auto-select effect below then picks the next real time.
  // A picker mounted on an empty grid has no day yet; the first grid picks one.
  const [reconciledSchedule, setReconciledSchedule] = useState(schedule);
  if (schedule !== reconciledSchedule) {
    setReconciledSchedule(schedule);
    if (!selectedDate || !availableDates.some((d) => d.isSame(selectedDate, 'day'))) {
      setSelectedDate(availableDates[0] ?? null);
      setSelectedTime(null);
    } else if (selectedTime && !availableTimes.some((t) => t.isSame(selectedTime))) {
      setSelectedTime(null);
    }
  }

  useEffect(() => {
    if (selectedDate && !selectedTime) {
      const monthKey = selectedDate.month().toString();
      const dayKey = selectedDate.date().toString();
      const times = tzSchedule[monthKey]?.[dayKey]?.map((slot) => slot.start) || [];
      if (times.length > 0) {
        const timeToSelect = onAutoSelectTime ? onAutoSelectTime(selectedDate, times) : times[0];
        if (timeToSelect) {
          setSelectedTime(timeToSelect);
        }
      }
    }
  }, [selectedDate, selectedTime, tzSchedule, onAutoSelectTime]);

  return {
    selectedDate,
    selectedTime,
    tzSchedule,
    months,
    days,
    availableDates,
    availableTimes,
    setSelectedDate,
    setSelectedTime,
    handleDateSelect,
    handleTimeSelect,
  };
}
